import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import { NextSeo } from 'next-seo'
import {
  AnalyticsPageType,
  ShopifyAnalyticsProduct,
} from '@shopify/hydrogen-react'
import { PrismicDocument, Query } from '@prismicio/types'
import { Breadcrumbs, Layout } from '@components/common'
import { ProductView, RecentlyViewedProducts } from '@components/product'
import commerce from '@lib/api/commerce'
import { productDetailsViewEvent } from '@lib/events/productDetailsViewEvent'
import getGlobalStaticProps from '@lib/get-global-static-props'
import { addRecentlyViewedProduct } from '@lib/local-storage'
import { createClient } from '@lib/prismicio'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { FaqBlock } from 'slices'
import { Product } from '../../types/Product'
import { getConfig } from '@framework/api'
import getProduct from '@framework/product/get-product'
import { findPromotionItemInfo } from '@lib/promotion/promotion'
import { useUI } from '@components/ui/context'
import AddonProducts from '@components/product/AddonProducts'

export async function getStaticProps({
  params,
  locale,
  locales,
  preview,
  previewData,
}: GetStaticPropsContext<{ slug: string }>) {
  const client = createClient({ previewData })
  const config = { locale, locales }
  const productPromise = commerce.getProduct({
    variables: { slug: params!.slug },
    config,
    preview,
  })
  const { product } = await productPromise
  if (!product) {
    return { notFound: true }
  }

  let defaultVariant = product.variants.find(
    (variant: Product.ProductVariant) => variant.quantityAvailable > 0
  )
  if (!defaultVariant) {
    defaultVariant = product.variants[0]
  }

  let globalProps: any = {}
  const globalPropsPromise = getGlobalStaticProps(client, locale)

  let productType = null

  let waterResistanceType = null

  if (product) {
    const productTypeFound: any = product?.tags?.find((tag: string) =>
      tag.startsWith('Type:')
    )

    const waterResistanceTypeTag: any = product?.tags?.find((tag: string) =>
      tag.startsWith('water_resistance:')
    )
    productType = productTypeFound?.replace('Type:', '')
    waterResistanceType = waterResistanceTypeTag?.replace(
      'water_resistance:',
      ''
    )
  }

  let pdpSpecificInfoDocument: PrismicDocument | { data: null } = { data: null }
  let pdpFaqDocument: PrismicDocument | { data: null } = { data: null }
  let pdpInfoDocument: PrismicDocument | { data: null } = { data: null }
  let waterResistanceDocument: PrismicDocument | { data: null } = { data: null }
  let sizeChartDocument: PrismicDocument | { data: null } = { data: null }
  let addonProducts: any[] | null = []

  const pdpInfoDocumentPromise = client.getByUID(
    'product-details-page',
    'pdp-generic-info'
  )

  const pdpSpecificInfoDocumentPromise = client.getByUID(
    'landing-page',
    `pdp--${params!.slug}`
  )

  const sizeChartDocumentPromise = client.getByUID(
    'sizechart',
    `sizechart-${productType}`
  )

  const faqType = productType || 'default'
  const faqDocumentPromise = client.getByTag(`faq__${faqType}`)
  const addonSKUs = product?.tags
    ?.filter((tag: string) => tag?.includes('addon'))
    .map((tag: string) => tag.split(':')[1])
  const addonProductsPromise = addonSKUs?.length
    ? commerce.getProductsBySKUs({
        skus: addonSKUs,
      })
    : new Promise((resolve) => resolve(null))

  const prismicDocumentPromises = [
    globalPropsPromise,
    pdpInfoDocumentPromise,
    pdpSpecificInfoDocumentPromise,
    sizeChartDocumentPromise,
    faqDocumentPromise,
    addonProductsPromise,
  ]

  if (productType == 'watches' && waterResistanceType) {
    const waterResistanceDocumentPromise = client.getByUID(
      'water-resistance',
      `water-resistance-${waterResistanceType}`
    )
    prismicDocumentPromises.push(waterResistanceDocumentPromise)
  }

  // TODO: Try using prismic .getByUIDS instead to get all the document in one request.
  await Promise.allSettled(prismicDocumentPromises).then((documents) => {
    globalProps =
      documents[0].status === 'fulfilled' ? documents[0].value : globalProps
    pdpInfoDocument =
      documents[1].status === 'fulfilled'
        ? (documents[1].value as PrismicDocument)
        : pdpInfoDocument
    pdpSpecificInfoDocument =
      documents[2].status === 'fulfilled'
        ? (documents[2].value as PrismicDocument)
        : pdpSpecificInfoDocument
    sizeChartDocument =
      documents[3].status === 'fulfilled'
        ? (documents[3].value as PrismicDocument)
        : sizeChartDocument

    if (documents[4].status === 'fulfilled') {
      const faqDocument = (documents[4].value as Query)
        .results[0] as PrismicDocument
      if (typeof faqDocument !== 'undefined') {
        pdpFaqDocument = faqDocument
      }
    }

    if (documents[5]) {
      addonProducts =
        documents[5].status === 'fulfilled'
          ? // @ts-ignore
            (documents[5]?.value?.products as unknown as [])
          : null
    }

    if (documents[6]) {
      waterResistanceDocument =
        documents[6].status === 'fulfilled'
          ? (documents[6].value as PrismicDocument)
          : waterResistanceDocument
    }
  })

  const giftWithPurchaseConfig = globalProps.giftWithPurchaseConfig
  const giftWithPurchaseProduct: any[] = []
  giftWithPurchaseConfig?.map((item: any) => {
    giftWithPurchaseProduct.push(item)
  })

  let promoProduct: any[] = []
  const promotionCheck = await findPromotionItemInfo(
    product?.tags!,
    giftWithPurchaseProduct
  )
    .then((result) => {
      if (result === undefined) {
        return null
      } else {
        promoProduct.push(result)
      }

      return result
    })
    .catch((error) => {
      console.log(error)
    })

  if (promotionCheck) {
    try {
      const config = getConfig({ locale })
      await getProduct({
        variables: {
          slug: promotionCheck.promotion.freeorDiscountedProduct || '',
        },
        config,
        preview,
      }).then((res: any) => promoProduct.push(res))
    } catch (err) {
      console.log(err)
    }
  }

  const productAnalytics: ShopifyAnalyticsProduct = {
    productGid: product.id,
    variantGid: defaultVariant.id.toString(),
    name: product.name,
    variantName: defaultVariant.sku,
    brand: product.vendor!,
    price: defaultVariant.price.toString(),
  }

  const productTitle: string = product.name.toLowerCase()
  const breadcrumbsMetafield: Product.MetaField | undefined =
    product.metafields?.find(
      (field: Product.MetaField) => field.key === 'breadcrumbs'
    )

  const breadcrumbOptions = breadcrumbsMetafield?.value
    ? JSON.parse(breadcrumbsMetafield.value)
    : []

  const { data: pdpFaqData }: PrismicDocument | { data: null } = pdpFaqDocument

  let faqBlockSlice: any = null
  if (pdpFaqData) {
    faqBlockSlice = (pdpFaqData as any).slices.find(
      (slice: any) => slice.slice_type === 'faq_block'
    )
  }

  let addonProductsFormatted = null
  if (addonProducts && addonProducts.length) {
    addonProductsFormatted = addonProducts
      ?.filter((addonProduct) => addonProduct.id !== product?.id)
      ?.map((product: any) => {
        delete product['origin']
        return product
      })
  }

  return {
    props: {
      ...globalProps,
      product,
      productTitle,
      breadcrumbOptions,
      sizeChartDocument,
      waterResistanceDocument,
      pdpInfoDocument,
      pdpSpecificInfoDocument,
      faqBlockSlice,
      addonProducts: addonProductsFormatted,
      promoProduct,
      analyticsPayload: {
        pageType: AnalyticsPageType.product,
        resourceId: product.id,
        products: [productAnalytics],
      },
      defaultVariant,
    },
    revalidate: 3600,
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  const { products } = await commerce.getAllProductPaths({
    variables: {
      first: 100,
      sortKey: 'BEST_SELLING',
    },
  })
  const paths = products.map((product: any) => ({
    params: {
      slug: product.path.replace('/', ''),
    },
  }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default function Slug({
  product,
  productTitle,
  breadcrumbOptions,
  sizeChartDocument,
  pdpInfoDocument,
  waterResistanceDocument,
  pdpSpecificInfoDocument,
  faqBlockSlice,
  addonProducts,
  promoProduct,
  defaultVariant,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter()
  const { openSidebar, setSidebarView } = useUI()
  const { id, slug }: Product.Product = product
  let selectedDefaultVariant = defaultVariant
  const { data: sizeChartData }: PrismicDocument | { data: null } =
    sizeChartDocument
  const { data: pdpInfoData }: PrismicDocument | { data: null } =
    pdpInfoDocument
  const { data: waterResistanceData }: PrismicDocument | { data: null } =
    waterResistanceDocument

  const { data: pdpSpecificInfoData }: PrismicDocument | { data: null } =
    pdpSpecificInfoDocument

  // Get a number of inventory available at a selected store
  useEffect(() => {
    if (product && id) {
      addRecentlyViewedProduct(product)
      productDetailsViewEvent(product)
    }
  }, [product])
  useEffect(() => {
    if (router.query?.support_menu_open === 'true') {
      setSidebarView('SERVICE_PORTAL_VIEW')
      openSidebar()
    }
  }, [router.query?.support_menu_open])
  return (
    <>
      <NextSeo
        canonical={`https://${process.env.NEXT_PUBLIC_SITE_DOMAIN}/products/${slug}`}
      />
      <div className="wrapper">
        {productTitle && (
          <Breadcrumbs current={productTitle} options={breadcrumbOptions} />
        )}
      </div>
      <ProductView
        product={product as any}
        sizeChartData={sizeChartData || null}
        waterResistanceData={waterResistanceData || null}
        productInfoDocs={(pdpInfoData as any)?.slices || []}
        pdpSpecificInfoDocument={(pdpSpecificInfoData as any)?.slices || []}
        promoProduct={promoProduct || null}
        defaultVariant={selectedDefaultVariant}
      />
      {addonProducts && <AddonProducts products={addonProducts} />}
      <RecentlyViewedProducts />
      {faqBlockSlice && (
        <div className="wrapper md:mt-32 md:mb-24 mb-14">
          <FaqBlock slice={faqBlockSlice} />
        </div>
      )}
    </>
  )
}

Slug.Layout = Layout
